import { useIntl } from '@capsicum/common';
import { AmountSelection, ButtonInSection, type ButtonProps, ButtonSection, useRouter } from '@capsicum/common-web';
import { useNCWSendChilizAmount } from '@capsicum/features/nc-wallet/shared';
import { convertNcwBalanceCurrencies, useNCWBalance } from '@capsicum/socios-core';
import { useEffect } from 'react';

import { NCWBalanceInfo } from '../../../ui';
import * as S from '../amountComponents.styled';

import { SendChilizAlert } from './SendChilizAlert';

/**
 * Part of the NCW Send Flow. Users can enter the Chiliz amount to be sent.
 */
export const NCWSendChilizAmount = () => {
	const { $t } = useIntl();
	const { chilizBalance, currencies, isPending: isBalancePending, ncwBalance } = useNCWBalance();
	// TODO: to remove; awaiting BE to fix currencies data type.
	const currenciesArray = convertNcwBalanceCurrencies(ncwBalance?.currencies);
	const chilizBalanceRaw = currenciesArray.find(c => c.currency.shortCode === 'CHZ');
	const { conversionAltPrice, convertedDefaultPrice, error, inputValue, intlText, setError, setInputValue } =
		useNCWSendChilizAmount(chilizBalanceRaw, currencies);

	const isAmountInvalid = !inputValue || inputValue < 1 || inputValue > Number(chilizBalance?.usableAmount);

	// Set the AmountSelection error if the entering amount is invalid
	useEffect(() => {
		if (isAmountInvalid) {
			setError({
				message: $t('ncWallet.send.enterAmount.invalidAmount', {
					maxValue: chilizBalance?.usableAmount,
					minValue: 1,
					shortCode: chilizBalance?.shortCode
				}),
				type: 'error'
			});
		} else {
			setError(undefined);
		}
	}, [isAmountInvalid]);

	// TODO: supply the estimated network fee when BE API is available.
	const insufficientChilizAmount = inputValue + 9.9 > Number(chilizBalance?.amount);

	const { push } = useRouter();
	const buttonProps = {
		disabled: isBalancePending || isAmountInvalid,
		isLoading: isBalancePending,
		onClick: () => {
			push('recipients');
		},
		testID: 'ncw-send-chilizAmount-continue-btn',
		text: intlText.buttonText
	} satisfies ButtonProps;

	return (
		<S.Wrapper>
			<S.Container>
				<S.Title variant="title5">{intlText.pageTitle}</S.Title>
				<NCWBalanceInfo
					altAmount={chilizBalance?.tokenCardData.usableFormattedAltBalance}
					balanceAmount={chilizBalance?.tokenCardData.usableFormattedDefaultBalance ?? ''}
					isLoading={isBalancePending}
				/>
				<AmountSelection
					altCurrencyAmount={conversionAltPrice}
					conversionPrice={convertedDefaultPrice}
					currencyShortName={chilizBalance?.shortCode}
					defaultCurrencyAmount={`1 ${chilizBalance?.shortCode}`}
					error={error}
					isConversionRatesLoading={isBalancePending}
					max={chilizBalance?.usableAmount}
					min={1}
					testID="ncw-select-tokenAmount-numberField"
					value={inputValue?.toString()}
					onChange={amount => setInputValue(amount)}
				/>
				{/*
					We will show network fees here at some point in the future
					<FeeAndTotalPrice inputValue={inputValue} />
				*/}
				<SendChilizAlert alertType="message" />
				<ButtonSection>
					<ButtonInSection {...buttonProps} />
				</ButtonSection>
			</S.Container>
		</S.Wrapper>
	);
};
